import { useCallback } from 'react';

import useWaApi from './axios/useWaApi';
import { OperationStatus } from '../store/models/workplace/OperationStatus';

export interface WaOrganizationBaseOption {
  code: string;
  name: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface WaOrganizationSquadOption extends WaOrganizationBaseOption {}

export interface WaOrganizationSectionOption extends WaOrganizationBaseOption {
  location?: WaOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
  squads: WaOrganizationSquadOption[];
}

export interface WaOrganizationSalesOfficeLocation {
  longitude: number;
  latitude: number;
}

export interface WaOrganizationSalesOfficeOption extends WaOrganizationBaseOption {
  aliasBranchCode: string;
  aliasSalesOfficeName: string;
  location: WaOrganizationSalesOfficeLocation;
  defaultScale?: number;
  searchRange?: number;
  sections: WaOrganizationSectionOption[];
}

export interface WaOrganizationBranchOption extends WaOrganizationBaseOption {
  salesOffices: WaOrganizationSalesOfficeOption[];
}

export interface WaOrganizationOptions {
  branches: WaOrganizationBranchOption[];
}

type WaOrganizationOptionsQueryParams = {
  branchCode?: string;
  salesOfficeCode?: string;
  sectionCode?: string;
  squadCode?: string;
};

export interface WaStatusOption {
  code: string;
  statusName: string;
}

export interface WaStatusOptionsGroup {
  groupName: OperationStatus;
  statuses: WaStatusOption[];
}

function useWaOptionsRepo() {
  const { get } = useWaApi();

  const getOrganizationOptionsRequest = useCallback(
    async (
      branchCode?: string,
      salesOfficeCode?: string,
      sectionCode?: string,
      squadCode?: string
    ) => {
      const params: Partial<WaOrganizationOptionsQueryParams> = {};
      if (branchCode) params.branchCode = branchCode;
      if (salesOfficeCode) params.salesOfficeCode = salesOfficeCode;
      if (sectionCode) params.sectionCode = sectionCode;
      if (squadCode) params.squadCode = squadCode;

      try {
        return (
          (await get<WaOrganizationOptions>('/api/wa/options/organization', {
            params: Object.keys(params).length ? params : undefined,
          })) ?? {
            branches: [],
          }
        );
      } catch (error) {
        console.log(error);
        return { branches: [] };
      }
    },
    [get]
  );

  const getStatusesOptionsRequest = useCallback(async () => {
    try {
      return (await get<WaStatusOptionsGroup[]>('/api/wa/options/statuses')) ?? [];
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [get]);

  return {
    getOrganizationOptionsRequest,
    getStatusesOptionsRequest,
  };
}

export default useWaOptionsRepo;
